<template>
    <div
        class="ppv2-fixed ppv2-inset-0 ppv2-z-50 ppv2-overflow-y-auto ppv2-bg-black ppv2-bg-opacity-50"
        @click.self="$emit('close')"
    >
        <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-min-h-screen ppv2-p-4">
            <div class="ppv2-bg-white ppv2-rounded-lg ppv2-shadow-xl ppv2-max-w-2xl ppv2-w-full ppv2-p-6">
                <!-- Header -->
                <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-mb-6">
                    <h3 class="ppv2-text-xl poppinsSemiBold ppv2-text-gray-900">Sélectionner les colonnes</h3>
                    <button @click="$emit('close')" class="ppv2-text-gray-400 hover:ppv2-text-gray-600 ppv2-transition-colors">
                        <svg class="ppv2-h-6 ppv2-w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- Colonnes disponibles -->
                <div class="ppv2-max-h-96 ppv2-overflow-y-auto ppv2-mb-6 ppv2-border ppv2-rounded-lg ppv2-p-4">
                    <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-3">
                        <label
                            v-for="column in availableColumns"
                            :key="column.id"
                            class="ppv2-flex ppv2-items-center ppv2-p-3 ppv2-border ppv2-rounded-lg ppv2-cursor-pointer ppv2-transition-all"
                            :class="{
                                'ppv2-bg-blue-50 ppv2-border-blue-500': isColumnSelected(column.id),
                                'hover:ppv2-bg-gray-50': !isColumnSelected(column.id),
                            }"
                        >
                            <input
                                type="checkbox"
                                :checked="isColumnSelected(column.id)"
                                @change="toggleColumn(column)"
                                class="ppv2-h-4 ppv2-w-4 ppv2-text-blue-600 ppv2-rounded ppv2-border-gray-300 focus:ppv2-ring-2 focus:ppv2-ring-blue-500"
                            />
                            <span class="ppv2-ml-3 ppv2-text-sm ppv2-text-gray-700 poppinsMedium">
                                {{ column.title }}
                            </span>
                        </label>
                    </div>
                </div>

                <!-- Info -->
                <div class="ppv2-mb-6 ppv2-text-sm ppv2-text-gray-600">
                    <span class="poppinsMedium">{{ localSelectedColumns.length }}</span> colonne(s) sélectionnée(s)
                </div>

                <!-- Actions -->
                <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-pt-4 ppv2-border-t">
                    <button
                        @click="resetToDefault"
                        class="ppv2-px-4 ppv2-py-2 ppv2-text-sm ppv2-text-gray-700 hover:ppv2-bg-gray-100 ppv2-rounded-lg ppv2-transition-colors"
                    >
                        <i class="fas fa-undo ppv2-mr-1"></i> Réinitialiser
                    </button>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <button
                            @click="$emit('close')"
                            class="ppv2-px-4 ppv2-py-2 ppv2-text-sm ppv2-text-gray-700 ppv2-border ppv2-rounded-lg hover:ppv2-bg-gray-50 ppv2-transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            @click="saveColumns"
                            :disabled="saving || localSelectedColumns.length === 0"
                            class="ppv2-px-4 ppv2-py-2 ppv2-text-sm ppv2-bg-blue-600 ppv2-text-white ppv2-rounded-lg hover:ppv2-bg-blue-700 disabled:ppv2-opacity-50 disabled:ppv2-cursor-not-allowed ppv2-transition-colors"
                        >
                            <i v-if="saving" class="fas fa-spinner fa-spin ppv2-mr-1"></i>
                            {{ saving ? 'Sauvegarde...' : 'Appliquer' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'InvoiceColumnsSelector',
    props: {
        availableColumns: {
            type: Array,
            required: true,
        },
        selectedColumns: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            localSelectedColumns: this.selectedColumns ? JSON.parse(JSON.stringify(this.selectedColumns)) : [],
            saving: false,
        }
    },
    mounted() {},
    methods: {
        isColumnSelected(columnId) {
            return this.localSelectedColumns.some((col) => col.column_id === columnId)
        },
        toggleColumn(column) {
            const index = this.localSelectedColumns.findIndex((col) => col.column_id === column.id)
            if (index > -1) {
                this.localSelectedColumns.splice(index, 1)
            } else {
                this.localSelectedColumns.push({
                    column_id: column.id,
                    data: column.data,
                    name: column.name,
                    class: column.class,
                    title: column.title,
                })
            }
        },
        resetToDefault() {
            // Colonnes par défaut
            const defaultNames = ['status', 'serial_number', 'order_id', 'customer', 'created_at', 'actions']
            this.localSelectedColumns = this.availableColumns
                .filter((col) => defaultNames.includes(col.name))
                .map((col) => ({
                    column_id: col.id,
                    data: col.data,
                    name: col.name,
                    class: col.class,
                    title: col.title,
                }))
        },
        async saveColumns() {
            if (this.localSelectedColumns.length === 0) {
                if (this.$toast) {
                    this.$toast.warning('Veuillez sélectionner au moins une colonne')
                } else {
                    alert('Veuillez sélectionner au moins une colonne')
                }
                return
            }

            this.saving = true
            try {
                const response = await fetch('/back-office/invoices-v2/save-columns', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({
                        columns: this.localSelectedColumns,
                    }),
                })

                const data = await response.json()

                if (response.ok && data.success) {
                    this.$emit('update', data.columns)
                    this.$emit('close')

                    // Notification de succès
                    if (this.$toast) {
                        this.$toast.success('Les colonnes ont été sauvegardées avec succès')
                    }
                } else {
                    // Erreur de validation ou autre erreur serveur
                    let errorMessage = 'Erreur lors de la sauvegarde des colonnes'

                    if (response.status === 422 && data.errors) {
                        // Erreurs de validation Laravel - afficher tous les messages
                        const errors = Object.values(data.errors).flat()
                        errorMessage = errors.join('\n')
                    } else if (data.message) {
                        errorMessage = data.message
                    }

                    if (this.$toast) {
                        this.$toast.error(errorMessage)
                    } else {
                        alert(errorMessage)
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.saving = false
            }
        },
    },
}
</script>
